var _ = require("../../uam/module.js")(module.exports, {
  namespace: "egt.reserve",
  title: "EGT Reserve",
  description: "Dedicated /reserve page — encouraging hero + the proven $9.57 founding-reservation wizard, rendered INLINE.",
  supportServer: true,
  fields: {
    reserveUrl: { title: "Reserve URL", type: "uri", description: "Reservation entry point.", required: false }
  }
});

var S = require("../helpers/egt.sections.js");

/* The dedicated /reserve page (WP-N2). The reserve wizard
 * (email -> verify -> about-you -> Stripe payment) now lives HERE, rendered
 * INLINE on the page — not in a dialog. post-keynote-page.js mounts the exact
 * same proven wizard (getWizardDoc + the same step handlers) into the on-page
 * host [data-reserve-inline] below (mountInlineReserveWizardAsync). This element
 * never touches the payment path; it only frames the founding reservation
 * honestly and provides the mount point + an encouraging hero.
 *
 * Honest framing (Decisions Q4 / R18): you RESERVE YOUR SPOT for when the
 * platform opens — priority access + your non-refundable $9.57 credited in
 * full toward launch. You
 * are NOT buying access now. No dates. Never the word "draft". Media UNDIMMED;
 * contrast comes from the frosted glass only. */
exports.render = async function render(options) {
  var reserveUrl = (options && options.reserveUrl) || "/reserve";
  /* betaCta:true — /reserve shows the shared "Join the Beta" nav CTA with the
   * timed word-whoosh, same as home/platform/intrepid/studio (founder ruling
   * 2026-08-08). navV2() renders the CTA; S.page() emits the whoosh client
   * (/src/egt.beta-cta.js) on exactly the beta-CTA surfaces. */
  return S.page(reserveUrl, [
    renderHero(),
    renderReassure(),
    renderFlow(),
    renderClose(),
    /* JSON-LD: WebPage + BreadcrumbList (SEO wave 2B-7) */
    S.pageSchema({ path: "/reserve", name: "Join the public beta — The Universe Platform", about: "/platform#app", crumbs: [ { name: "Platform", path: "/platform" }, { name: "Join the public beta" } ] })
  ], { betaCta: true, footerCaptureLabel: S.FOOTER_LABELS.platform });
};

/* HERO (F40) — TWO PATHS onto the same platform, framed honestly over the
 * undimmed platform canvas (frosted glass only; no scrim). The platform is
 * ALREADY RUNNING: agency (Forge) clients build on it now, inside real Forge
 * projects (a limited public beta); self-serve is what everyone else reserves
 * a lane for as it opens (agents.md §4 AMENDED 2026-07-24). Both paths read as
 * intentional, distinguished siblings:
 *   Path one  — "Use it now, through Forge" → the project intake at /forge/start
 *               (secondary / ghost CTA).
 *   Path two  — "Reserve your lane" → the inline $9.57 reservation wizard at
 *               #reserve-wizard (primary / solid-gold CTA; keeps the three
 *               founding-reservation chips).
 * F34r: no "private beta" anywhere — the eyebrow + copy carry the "public
 * beta" framing (the meta title/description live in app/pages/reserve.json,
 * already corrected to public beta / $9.57). The "clients are using it now"
 * claim is present-tense and TRUE — load-bearing for trademark use-in-commerce.
 * No published dates. Media UNDIMMED. */
function renderHero() {
  return {
    n: "section", c: "egt-section egt-canvas egt-canvas--center egt-canvas--tall egt-reserve-hero",
    i: [
      S.media({ img: "/imgs/egt-platform-canvas.jpg", alt: "", parallax: 0.08, priority: true }),
      { n: "div", c: "egt__shell egt-canvas__inner egt-reserve-hero__inner",
        i: S.glass({ width: "wide", tone: "dark", c: "egt-reserve-hero__card egt-reveal",
          content: [
            { n: "p", c: "egt__eyebrow", i: "The Universe Platform · public beta" },
            { n: "h1", c: "egt-reserve-hero__title",
              i: [ "It's already running. ", { n: "span", c: "egt__gold", i: "Two ways in." } ] },
            { n: "p", c: "egt-reserve-hero__lead",
              i: "Agency clients are already building on the platform — inside real Forge projects. Come in through a project now, or reserve your lane for self-serve access as it opens." },
            /* the two paths — a balanced pair of frosted sub-cards. Path two
             * (reserve) carries the gold-tinted treatment + the solid-gold
             * primary CTA so the pair reads as clearly-distinguished siblings,
             * not two stacked buttons. */
            { n: "div", c: "egt-reserve-paths",
              i: [
                /* PATH ONE — use it now, through a Forge project. Secondary
                 * (ghost) CTA to the project intake wizard at /forge/start. */
                { n: "div", c: "egt-reserve-path egt-reserve-path--forge",
                  i: [
                    { n: "p", c: "egt-reserve-path__kicker", i: "Path one" },
                    { n: "h2", c: "egt-reserve-path__title", i: "Use it now, through Forge" },
                    { n: "p", c: "egt-reserve-path__copy",
                      i: "Forge takes on the project — building your app, running a campaign, a larger agency-level engagement — and you're on the platform from day one, as part of the work." },
                    { n: "div", c: "egt-reserve-path__actions",
                      i: { n: "a", href: "/forge/start", c: "egt-cta egt-cta--ghost egt-reserve-path__cta",
                        i: [ "Start a Forge project", { n: "span", c: "egt-cta__tick", i: "›" } ] } }
                  ] },
                /* PATH TWO — reserve your lane. The primary path: gold-tinted
                 * card, the three founding-reservation chips, and the solid-gold
                 * CTA that scrolls to the inline $9.57 wizard (#reserve-wizard,
                 * smooth-scrolled by setupAnchorLinks in post-keynote-page.js). */
                { n: "div", c: "egt-reserve-path egt-reserve-path--reserve",
                  i: [
                    { n: "p", c: "egt-reserve-path__kicker", i: "Path two" },
                    { n: "h2", c: "egt-reserve-path__title", i: "Reserve your lane" },
                    { n: "p", c: "egt-reserve-path__copy",
                      i: "Want to drive it yourself when self-serve opens? Reserve your spot — a $9.57 reservation holds your founding place." },
                    /* the three quiet founding-reservation chips (kept from N2). */
                    { n: "ul", c: "egt-reserve-hero__chips egt-reserve-path__chips",
                      i: [
                        { n: "li", c: "egt-reserve-chip", i: [ { n: "span", c: "egt-reserve-chip__k", i: "First in line" }, { n: "span", c: "egt-reserve-chip__v", i: "Provisioned ahead of everyone else." } ] },
                        { n: "li", c: "egt-reserve-chip", i: [ { n: "span", c: "egt-reserve-chip__k", i: "$9.57 reservation" }, { n: "span", c: "egt-reserve-chip__v", i: "Non-refundable, credited in full toward your launch." } ] },
                        { n: "li", c: "egt-reserve-chip", i: [ { n: "span", c: "egt-reserve-chip__k", i: "No commitment" }, { n: "span", c: "egt-reserve-chip__v", i: "Nothing beyond the reservation itself." } ] }
                      ] },
                    { n: "div", c: "egt-reserve-path__actions",
                      i: { n: "a", href: "#reserve-wizard", c: "egt-cta egt-cta--primary egt-reserve-path__cta egt-reserve-hero__cta",
                        i: [ "Reserve your lane", { n: "span", c: "egt-cta__tick", i: "›" } ] } }
                  ] }
              ] },
            { n: "p", c: "egt-reserve-hero__note",
              i: "Takes about a minute · secure payment · we email you the moment your founding sandbox is ready." },
            /* THE FREE PATH, surfaced (SEO wave 2C-10): one quiet line under the
             * paid paths — the reservation stays first — pointing to the footer's
             * free newsletter door (#egt-cap-footer, the double-opt-in capture
             * S.page renders on this page). Nothing removed or demoted. */
            { n: "p", c: "egt-reserve-hero__free",
              i: [ "Not ready to reserve? ",
                { n: "a", href: "#egt-cap-footer", c: "egt-reserve-hero__free-link", i: "Get the email when self-serve opens" }, "." ] }
          ] }) }
    ]
  };
}

/* REASSURANCE — three frosted cards of substance that meet the fence-sitter's
 * real hesitation head-on. Honest, app-grounded; no dates, no "access now". */
function renderReassure() {
  return S.section({
    eyebrow: "Why reserve now",
    title: "A small, honest step — with a real head start.",
    lead: "Reserving isn't a leap of faith. It's the calm way to make sure you're among the first through the door.",
    body: [
      S.featureGrid([
        { title: "You move to the front",
          copy: "Reservation holders are provisioned first. Your founding seat is already saved — you're not refreshing a waitlist." },
        { title: "Every dollar comes with you",
          copy: "The $9.57 isn't a separate cost. It's non-refundable, and it's credited in full to your account when you start — it simply holds your founding place." },
        { title: "You hear it straight from us",
          copy: "Founding members hear from the team directly — the latest from the studio, and the moment your invitation is ready." }
      ])
    ]
  });
}

/* THE FLOW — the inline reserve wizard, in a frosted card, mounted by
 * post-keynote-page.js into [data-reserve-inline]. Also carries the graceful
 * 503 "be first in line" fallback (hidden by default; the boot pre-flight swaps
 * to it if Stripe keys aren't set yet, so no one lands on a payment form that
 * can't complete). The section id is the scroll target for the hero + close CTAs. */
function renderFlow() {
  return {
    n: "section", c: "egt-section egt-reserve-flow", id: "reserve-wizard",
    i: { n: "div", c: "egt__shell",
      i: [
        /* live reservation block */
        { n: "div", c: "egt-reserve-flow__live", "data-egt-reserve-live": "true",
          i: {
            n: "div", c: "egt-glass egt-glass--wide egt-reserve-flow__card egt-reveal",
            i: [
              { n: "p", c: "egt__eyebrow", i: "Reserve your founding spot" },
              { n: "h2", c: "egt-reserve-flow__title", i: "Hold your place — $9.57." },
              { n: "p", c: "egt-reserve-flow__lead",
                i: "Confirm your email, tell us a little about you, and secure your reservation — about a minute." },
              /* MOUNT POINT — post-keynote-page.js renders the proven ua.wizard
               * here (email -> verify -> about-you -> Stripe). Empty until JS
               * mounts it; a no-JS visitor still sees the honest framing above. */
              { n: "div", c: "egt-reserve-flow__host", "data-reserve-inline": "true",
                i: { n: "p", c: "egt-reserve-flow__loading", role: "status", i: "Loading your reservation…" } }
            ]
          } },
        /* graceful 503 state — hidden by default, revealed by egt.reserve.boot.js
         * if Stripe keys aren't set yet. Honest, calm, points at the footer
         * newsletter. No payment shown. */
        { n: "div", c: "egt-reserve-soon", "data-egt-reserve-soon": "true", hidden: true,
          i: S.glass({ width: "wide", tone: "dark", c: "egt-reserve-soon__card",
            content: [
              { n: "p", c: "egt__eyebrow", i: "The Universe Platform by EGT" },
              { n: "h2", c: "egt-reserve-flow__title",
                i: [ "Be first in ", { n: "span", c: "egt__gold", i: "line." } ] },
              { n: "p", c: "egt-reserve-flow__lead",
                i: "Leave your email and we'll make sure you're first to your founding reservation in the public Beta." },
              { n: "div", c: "egt-reserve-hero__actions",
                i: { n: "a", href: "#egt-cap-footer", c: "egt-cta egt-reserve-hero__cta",
                  i: [ "Be first to know", { n: "span", c: "egt-cta__tick", i: "›" } ] } }
            ] }) }
      ] }
  };
}

/* CLOSE — a quiet closing card that sends fence-sitters back up to the wizard. */
function renderClose() {
  return {
    n: "section", c: "egt-section egt-section--center egt-reserve-close",
    i: { n: "div", c: "egt__shell",
      i: { n: "div", c: "egt-glass egt-glass--wide egt-reserve-close__card egt-reveal",
        i: [
          { n: "h2", c: "egt-reserve-close__title", i: "Save your place." },
          { n: "p", c: "egt-reserve-close__lead",
            i: "Join the Beta and you're on the founding list — first in line as self-serve opens." },
          { n: "div", c: "egt-reserve-close__actions",
            i: { n: "a", href: "#reserve-wizard", c: "egt-cta egt-reserve-close__cta",
              i: [ "Reserve my spot", { n: "span", c: "egt-cta__tick", i: "›" } ] } }
        ] } }
  };
}
